import { useRouter } from "next/router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Auth } from "aws-amplify";
import * as Sentry from "@sentry/nextjs";

import { useSession } from "@/components/providers/session";
import Loader from "@/components/assets/loader.svg";
import { Svg } from "@/components/svg";

export default function ForgotPassword() {
  const session = useSession();
  const router = useRouter();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const onSubmit = async (data) => {
    setLoading(true);
    setError(false);
    try {
      await Auth.forgotPassword(data.email.toLowerCase());
      router.push("/reset-password/");
    } catch (err) {
      console.log(err);
      setLoading(false);
      setError(true);
      setErrorMessage(err.message);
      Sentry.captureException(err);
    }
  };

  return (
    <>
      {loading && (
        <div className="fixed inset-0 z-50 flex justify-center items-center">
          <div className="relative z-50">
            {/* @ts-ignore */}
            <Svg
              data={Loader}
              // @ts-ignore
              width={64}
              height={64}
              stroke="#75ccc9"
              fill="#fff"
            />
          </div>
          <div className="fixed inset-0 bg-white opacity-25 z-40" />
        </div>
      )}
      <div className="flex justify-center -mt-14 pb-24 md:py-12 px-4 sm:px-6 lg:px-8 lg:pt-8 lg:pb-24">
        <div className="relative w-full mx-auto ">
          <svg
            className="absolute right-full bottom-0 transform -translate-x-1/2"
            width="404"
            height="404"
            fill="none"
            viewBox="0 0 404 404"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="85737c0e-0916-41d7-917f-596dc7edfa27"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x="0"
                  y="0"
                  width="4"
                  height="4"
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width="404"
              height="404"
              fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)"
            />
          </svg>

          <div className="max-w-lg w-full space-y-8 mx-auto lg:px-8">
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="mt-8 space-y-6"
              action="#"
              method="post"
            >
              {error && (
                <div className="pb-2 text-center">
                  {!errorMessage ? (
                    <p className="text-red-500 pt-2 text-sm block">
                      There was an error processing your request. Please try
                      again
                    </p>
                  ) : (
                    <p className="text-red-500 pt-2 text-sm block">
                      {errorMessage}
                    </p>
                  )}
                </div>
              )}
              <input type="hidden" name="remember" value="true" />
              <div>
                <div>
                  <label htmlFor="email-address" className="sr-only">
                    Email address
                  </label>
                  <input
                    id="email-address"
                    name="email"
                    type="email"
                    autoFocus
                    autoComplete="email"
                    {...register("email", { required: true })}
                    className={`block w-full shadow py-3 px-4 placeholder-gray-500 focus:ring-brand-500 focus:border-brand-500 ${
                      errors.email ? "border-red-500" : "border-gray-300"
                    } rounded-md`}
                    placeholder="Email address"
                  />
                </div>
                {errors.email && (
                  <span className="text-red-500 pt-2 text-sm block">
                    Email is required
                  </span>
                )}
              </div>

              <div>
                <button
                  type="submit"
                  className="group relative w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow text-base font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
                >
                  <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                    <svg
                      className="h-5 w-5 text-brand-500 group-hover:text-brand-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                  Reset password
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
